import { Component, Input } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { DataFormModel } from '../../models/data-form-model.model';
import { FaqGroup } from '../../models/faq-group.model';
import { Platform } from '../../models/platform.model';

import { FaqGroupService } from '../../services/faq-group.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'app-faq-group-modal',
  templateUrl: './faq-group-modal.component.html',
  styleUrl: './faq-group-modal.component.scss'
})
export class FaqGroupModalComponent {

  @Input() platform!: Platform;
  @Input() faqGroup?: FaqGroup;

  /* FAQ_GROUPS.title es VARCHAR(35) y el backend NO valida el largo: si se pasa
     revienta con 500. El tope se corta aquí. */
  public readonly titleMaxLength: number = 35;

  public dataForm: DataFormModel = {
    title: {
      value: '',
      type: 'text',
      label: 'Título del grupo',
      placeholder: 'Pases para visitas',
      requiredMessage: 'Por favor, ingrese el título del grupo',
      required: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.titleMaxLength)
      ]
    },
    status: {
      /* Valor por defecto de un alta, no autorrelleno: en edición se sobrescribe
         con el dato real en loadData(). */
      value: true,
      type: 'switch',
      label: 'Visible en la app',
      required: false,
      validators: []
    },
  };

  public formFaqGroup!: FormGroup;
  public loading: boolean = false;
  public submitted: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    public faqGroupService: FaqGroupService,
    public utilService: UtilService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  get f() {
    return this.formFaqGroup.controls;
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create() {

    const flag = await this.faqGroupService.create(
      this.platform,
      {
        title: `${this.formFaqGroup.value.title ?? ''}`.trim(),
        status: this.formFaqGroup.value.status === true
      }
    );

    if(flag) {
      this.activeModal.close();
    }

  }

  /*
  ██╗      ██████╗  █████╗ ██████╗ ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  async loadData() {
    this.loading = true;

    /* Solo se precarga cuando el modal se abrió SOBRE un grupo existente.
       En alta el título queda vacío: nunca se autorrellena nada que pueda
       sobrescribir un dato ya capturado. */
    if(this.faqGroup) {

      this.dataForm['title'].value = this.faqGroup.title;
      this.dataForm['status'].value = this.faqGroup.status;

    }

    this.formFaqGroup = this.utilService.buildForm(this.dataForm);

    this.loading = false;
  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  async onSubmit() {

    /* El botón de envío SIEMPRE está habilitado: la validación corre aquí y el
       error se pinta debajo del campo que lo provocó. */
    this.submitted = true;

    if(this.formFaqGroup.invalid) {
      return;
    }

    if(this.faqGroup) {
      await this.update();
    } else {
      await this.create();
    }

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update() {

    /* El PUT no es parcial: title y status van SIEMPRE los dos. */
    const flag = await this.faqGroupService.update(
      this.platform,
      this.faqGroup!.faqGroupId,
      {
        title: `${this.formFaqGroup.value.title ?? ''}`.trim(),
        status: this.formFaqGroup.value.status === true
      }
    );

    if(flag) {
      this.activeModal.close();
    }

  }

}
